/**
 * ╔═══════════════════════════════════════════════════╗
 * ║  AudioManager — Epic Battle Sound System         ║
 * ║  Procedural Web Audio: zero external files       ║
 * ╚═══════════════════════════════════════════════════╝
 *
 * Sounds:
 *   hit()           — melee/rock impact, deep punchy
 *   block()         — paper blocks a rock, heavy clang
 *   rockThrow()     — powerful throw whoosh
 *   rockImpact()    — rock hits ground with debris
 *   scissorsSwing() — fast blade slash
 *   scissorsCrit()  — scissors cuts paper, dramatic tear
 *   victory()       — round/match win fanfare
 *   countdown()     — ominous countdown tick
 *   fight()         — round start horn blast
 *   death()         — player KO dramatic fall
 *   walk()          — footstep on sand
 *   dash()          — whoosh burst for dash
 *   zoneWarning()   — alarm siren for zone closing
 *
 * Music:
 *   startMusic()    — dynamic layered combat music
 *   stopMusic()
 */

let ctx = null;
let musicGain = null;
let masterGain = null;
let sfxGain = null;
let musicPlaying = false;
let musicNodes = [];

function ensureCtx() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 1.0;
    masterGain.connect(ctx.destination);

    sfxGain = ctx.createGain();
    sfxGain.gain.value = 0.65;
    sfxGain.connect(masterGain);

    musicGain = ctx.createGain();
    musicGain.gain.value = 0.22;
    musicGain.connect(masterGain);
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// ── Noise buffers (cached) ───────────────────────
let _whiteNoiseCache = null;
let _pinkNoiseCache = null;

function noiseBuffer(duration, type = 'white') {
  const c = ensureCtx();
  if (type === 'white' && duration <= 1 && _whiteNoiseCache) return _whiteNoiseCache;
  if (type === 'pink' && duration <= 1 && _pinkNoiseCache) return _pinkNoiseCache;

  const len = c.sampleRate * Math.max(duration, 1);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  if (type === 'white') {
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    if (duration <= 1) _whiteNoiseCache = buf;
  } else if (type === 'pink') {
    let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      b0=0.99886*b0+w*0.0555179; b1=0.99332*b1+w*0.0750759;
      b2=0.96900*b2+w*0.1538520; b3=0.86650*b3+w*0.3104856;
      b4=0.55000*b4+w*0.5329522; b5=-0.7616*b5-w*0.0168980;
      data[i]=(b0+b1+b2+b3+b4+b5+b6+w*0.5362)*0.11;
      b6=w*0.115926;
    }
    if (duration <= 1) _pinkNoiseCache = buf;
  }
  return buf;
}

// ── Helpers ──────────────────────────────────────
function playTone(freq, duration, type = 'sine', volume = 0.3, startTime = 0, dest = null) {
  const c = ensureCtx();
  const t = startTime || c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.connect(gain);
  gain.connect(dest || sfxGain);
  osc.start(t);
  osc.stop(t + duration);
}

function noiseBurst(duration, freq, Q, volume = 0.3, startTime = 0, dest = null) {
  const c = ensureCtx();
  const t = startTime || c.currentTime;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(duration);
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(freq, t);
  filter.Q.setValueAtTime(Q, t);
  const gain = c.createGain();
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(dest || sfxGain);
  src.start(t);
  src.stop(t + duration);
}

function distortedImpact(freq, duration, volume = 0.5, startTime = 0) {
  const c = ensureCtx();
  const t = startTime || c.currentTime;
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.2, t + duration);

  const waveshaper = c.createWaveShaper();
  const curve = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const x = (i / 128) - 1;
    curve[i] = Math.tanh(x * 3);
  }
  waveshaper.curve = curve;

  const gain = c.createGain();
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.connect(waveshaper);
  waveshaper.connect(gain);
  gain.connect(sfxGain);
  osc.start(t);
  osc.stop(t + duration);
}

// ═══════════════════════════════════════════════════════
//  SOUND EFFECTS — Battle-grade
// ═══════════════════════════════════════════════════════

/** Melee/projectile hit — deep punchy impact with sub-bass thud */
export function hit() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Sub-bass thud
  const sub = c.createOscillator();
  const subG = c.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(80, t);
  sub.frequency.exponentialRampToValueAtTime(25, t + 0.2);
  subG.gain.setValueAtTime(0.7, t);
  subG.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
  sub.connect(subG); subG.connect(sfxGain);
  sub.start(t); sub.stop(t + 0.25);

  // Mid punch
  distortedImpact(200, 0.12, 0.4, t);

  // Crack/snap transient
  noiseBurst(0.04, 3500, 5, 0.5, t);
  noiseBurst(0.08, 1800, 2, 0.35, t + 0.01);

  // Flesh-like mid thud
  playTone(120, 0.08, 'triangle', 0.3, t);
}

/** Paper blocks a rock — heavy metallic shield clang with reverb */
export function block() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Deep metal clang
  playTone(280, 0.4, 'triangle', 0.35, t);
  playTone(560, 0.3, 'sine', 0.2, t + 0.005);
  playTone(1120, 0.25, 'sine', 0.1, t + 0.01);

  // Shimmering high ring
  playTone(1680, 0.5, 'sine', 0.08, t + 0.02);
  playTone(2240, 0.4, 'sine', 0.05, t + 0.03);

  // Impact noise
  noiseBurst(0.06, 2000, 3, 0.3, t);

  // Low resonant boom
  const boom = c.createOscillator();
  const bG = c.createGain();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(150, t);
  boom.frequency.exponentialRampToValueAtTime(50, t + 0.15);
  bG.gain.setValueAtTime(0.35, t);
  bG.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
  boom.connect(bG); bG.connect(sfxGain);
  boom.start(t); boom.stop(t + 0.2);
}

/** Rock throw — powerful whoosh with grunt */
export function rockThrow() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Layered whoosh
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(0.35);
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(300, t);
  filter.frequency.exponentialRampToValueAtTime(3000, t + 0.12);
  filter.frequency.exponentialRampToValueAtTime(600, t + 0.35);
  filter.Q.setValueAtTime(6, t);
  const g = c.createGain();
  g.gain.setValueAtTime(0.45, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
  src.connect(filter); filter.connect(g); g.connect(sfxGain);
  src.start(t); src.stop(t + 0.35);

  // Effort grunt
  playTone(110, 0.12, 'sawtooth', 0.12, t);
  playTone(90, 0.1, 'sine', 0.15, t + 0.02);

  // Weight thump as arm releases
  distortedImpact(160, 0.06, 0.15, t + 0.05);
}

/** Rock hits ground — heavy debris impact */
export function rockImpact() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Deep heavy thud
  const thick = c.createOscillator();
  const tG = c.createGain();
  thick.type = 'sine';
  thick.frequency.setValueAtTime(90, t);
  thick.frequency.exponentialRampToValueAtTime(22, t + 0.25);
  tG.gain.setValueAtTime(0.5, t);
  tG.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
  thick.connect(tG); tG.connect(sfxGain);
  thick.start(t); thick.stop(t + 0.3);

  // Debris scatter — multiple small impacts
  for (let i = 0; i < 3; i++) {
    noiseBurst(0.05 + Math.random() * 0.05, 1200 + Math.random() * 800, 3, 0.15, t + i * 0.03);
  }

  // Dust cloud noise
  noiseBurst(0.2, 600, 1, 0.2, t + 0.03);
}

/** Scissors melee swing — sharp blade whoosh */
export function scissorsSwing() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Sharp fast whoosh
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(0.18);
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(800, t);
  filter.frequency.exponentialRampToValueAtTime(5000, t + 0.06);
  filter.frequency.exponentialRampToValueAtTime(2000, t + 0.18);
  filter.Q.setValueAtTime(10, t);
  const g = c.createGain();
  g.gain.setValueAtTime(0.35, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
  src.connect(filter); filter.connect(g); g.connect(sfxGain);
  src.start(t); src.stop(t + 0.18);

  // Metallic shing
  playTone(4000, 0.06, 'sine', 0.12, t + 0.01);
  playTone(6000, 0.04, 'sine', 0.06, t + 0.02);

  // Blade resonance
  playTone(2200, 0.1, 'triangle', 0.08, t + 0.03);
}

/** Scissors critical — cuts paper, dramatic tear */
export function scissorsCrit() {
  const c = ensureCtx();
  const t = c.currentTime;

  scissorsSwing();

  // Dramatic tearing noise
  noiseBurst(0.3, 2000, 2, 0.5, t + 0.03);

  // Low dramatic boom
  distortedImpact(70, 0.35, 0.45, t + 0.05);

  // High impact ring
  playTone(3200, 0.25, 'triangle', 0.2, t + 0.04);

  // Shredding paper layers
  for (let i = 0; i < 3; i++) {
    noiseBurst(0.08, 3000 + i * 1000, 4, 0.15, t + 0.05 + i * 0.04);
  }
}

/** Victory fanfare — heroic ascending brass */
export function victory() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Heroic brass arpeggio (C major)
  const notes = [261.63, 329.63, 392.00, 523.25];
  notes.forEach((freq, i) => {
    const delay = i * 0.18;
    playTone(freq, 0.8, 'sawtooth', 0.15, t + delay);
    playTone(freq, 0.8, 'triangle', 0.2, t + delay);
    playTone(freq * 0.5, 0.6, 'sine', 0.12, t + delay);
  });

  // Sustained power chord
  const ct = t + 0.75;
  playTone(261.63, 1.8, 'triangle', 0.18, ct);
  playTone(329.63, 1.8, 'sine', 0.14, ct);
  playTone(392.00, 1.8, 'triangle', 0.14, ct);
  playTone(523.25, 1.5, 'sine', 0.12, ct);

  // War drum roll
  for (let i = 0; i < 8; i++) {
    const dt2 = ct + i * 0.06;
    distortedImpact(60, 0.1, 0.12 + i * 0.02, dt2);
  }

  // Cymbal shimmer
  noiseBurst(1.5, 5000, 1, 0.08, ct);

  // Sparkles
  for (let i = 0; i < 8; i++) {
    playTone(2000 + Math.random() * 3000, 0.12, 'sine', 0.04, ct + 0.5 + i * 0.06);
  }
}

/** Countdown tick — ominous deep tick */
export function countdown() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Deep resonant tick
  playTone(220, 0.15, 'triangle', 0.3, t);
  playTone(110, 0.2, 'sine', 0.25, t);

  // Metallic click
  noiseBurst(0.02, 5000, 8, 0.2, t);

  // Sub pulse
  playTone(55, 0.3, 'sine', 0.15, t);
}

/** FIGHT! — dramatic war horn blast */
export function fight() {
  const c = ensureCtx();
  const t = c.currentTime;

  // War horn — thick layered brass
  playTone(146.83, 0.6, 'sawtooth', 0.3, t);
  playTone(146.83, 0.6, 'triangle', 0.25, t);
  playTone(220, 0.5, 'sawtooth', 0.2, t);
  playTone(293.66, 0.4, 'triangle', 0.15, t);

  // Sub bass impact
  distortedImpact(80, 0.3, 0.5, t);

  // Cymbal crash
  noiseBurst(0.5, 4000, 1, 0.25, t);
  noiseBurst(0.3, 8000, 2, 0.15, t);

  // Rising sweep
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(600, t + 0.2);
  g.gain.setValueAtTime(0.2, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
  osc.connect(g); g.connect(sfxGain);
  osc.start(t); osc.stop(t + 0.25);
}

/** Player KO — dramatic descending doom */
export function death() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Deep descending doom
  distortedImpact(250, 0.5, 0.35, t);
  distortedImpact(150, 0.6, 0.3, t + 0.1);
  playTone(80, 0.8, 'sine', 0.4, t + 0.15);

  // Body hit
  noiseBurst(0.15, 800, 1.5, 0.35, t + 0.05);

  // Dramatic reverb tail
  playTone(60, 1.2, 'sine', 0.2, t + 0.3);
}

/** Footstep on sand */
export function walk() {
  const c = ensureCtx();
  const t = c.currentTime;
  noiseBurst(0.05, 700 + Math.random() * 500, 3, 0.06, t);
  playTone(50 + Math.random() * 30, 0.03, 'sine', 0.04, t);
}

/** Dash — quick burst of speed whoosh */
export function dash() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Fast directional whoosh
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(0.25);
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(500, t);
  filter.frequency.exponentialRampToValueAtTime(4000, t + 0.08);
  filter.frequency.exponentialRampToValueAtTime(800, t + 0.25);
  filter.Q.setValueAtTime(5, t);
  const g = c.createGain();
  g.gain.setValueAtTime(0.4, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
  src.connect(filter); filter.connect(g); g.connect(sfxGain);
  src.start(t); src.stop(t + 0.25);

  // Air compression burst
  playTone(200, 0.06, 'sine', 0.2, t);
  playTone(400, 0.04, 'triangle', 0.1, t + 0.01);
}

/** Zone closing alarm */
export function zoneWarning() {
  const c = ensureCtx();
  const t = c.currentTime;

  // Two-tone alarm siren
  for (let i = 0; i < 3; i++) {
    const st = t + i * 0.5;
    playTone(880, 0.2, 'square', 0.15, st);
    playTone(660, 0.2, 'square', 0.15, st + 0.25);
  }

  // Deep warning pulse
  playTone(55, 1.0, 'sine', 0.2, t);
  distortedImpact(100, 0.3, 0.2, t);
}

// ═══════════════════════════════════════════════════════
//  BACKGROUND MUSIC — Dynamic Layered Combat System
//  Bass drone + War drums + Tense strings + Battle melody
// ═══════════════════════════════════════════════════════

let musicInterval = null;

export function startMusic() {
  if (musicPlaying) return;
  musicPlaying = true;
  const c = ensureCtx();

  // ── Layer 1: Bass drone (ominous power chord) ──
  const bassOsc1 = c.createOscillator();
  const bassOsc2 = c.createOscillator();
  const bassGain = c.createGain();
  bassOsc1.type = 'sawtooth';
  bassOsc1.frequency.setValueAtTime(55, c.currentTime);
  bassOsc2.type = 'sine';
  bassOsc2.frequency.setValueAtTime(55, c.currentTime);
  bassGain.gain.setValueAtTime(0.08, c.currentTime);

  const bassFilter = c.createBiquadFilter();
  bassFilter.type = 'lowpass';
  bassFilter.frequency.setValueAtTime(150, c.currentTime);
  bassOsc1.connect(bassFilter);
  bassOsc2.connect(bassFilter);
  bassFilter.connect(bassGain);
  bassGain.connect(musicGain);
  bassOsc1.start(); bassOsc2.start();
  musicNodes.push(bassOsc1, bassOsc2);

  // ── Layer 2: Sub-bass pulse ──
  const subOsc = c.createOscillator();
  const subGain = c.createGain();
  subOsc.type = 'sine';
  subOsc.frequency.setValueAtTime(27.5, c.currentTime);
  subGain.gain.setValueAtTime(0.06, c.currentTime);
  subOsc.connect(subGain);
  subGain.connect(musicGain);
  subOsc.start();
  musicNodes.push(subOsc);

  // ── Rhythmic engine ──
  const BPM = 110;
  const beatMs = (60 / BPM) * 1000;

  // D minor scale frequencies
  const scale = [
    73.42, 82.41, 87.31, 98.00, 110.00, 116.54, 130.81,
    146.83, 164.81, 174.61, 196.00, 220.00, 233.08, 261.63,
    293.66, 329.63, 349.23, 392.00, 440.00
  ];

  let beatCount = 0;
  let currentPhrase = 0;

  musicInterval = setInterval(() => {
    if (!musicPlaying) return;
    const c2 = ensureCtx();
    const t = c2.currentTime;
    const beat = beatCount % 16;
    const bar = Math.floor(beatCount / 16);

    // ── War drums (layered kick pattern) ──
    if (beat === 0 || beat === 4 || beat === 8 || beat === 12) {
      const kick = c2.createOscillator();
      const kG = c2.createGain();
      kick.type = 'sine';
      kick.frequency.setValueAtTime(beat === 0 ? 100 : 90, t);
      kick.frequency.exponentialRampToValueAtTime(25, t + 0.2);
      kG.gain.setValueAtTime(beat === 0 ? 0.3 : 0.22, t);
      kG.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      kick.connect(kG); kG.connect(musicGain);
      kick.start(t); kick.stop(t + 0.25);

      noiseBurst(0.015, 5000, 8, 0.12, t, musicGain);
    }

    // Snare on backbeats
    if (beat === 4 || beat === 12) {
      noiseBurst(0.12, 2500, 3, 0.14, t, musicGain);
      const snr = c2.createOscillator();
      const sG = c2.createGain();
      snr.type = 'triangle';
      snr.frequency.setValueAtTime(200, t);
      snr.frequency.exponentialRampToValueAtTime(120, t + 0.08);
      sG.gain.setValueAtTime(0.12, t);
      sG.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      snr.connect(sG); sG.connect(musicGain);
      snr.start(t); snr.stop(t + 0.1);
    }

    // Hi-hat every beat
    {
      const isOpen = beat % 2 === 1;
      const dur = isOpen ? 0.12 : 0.04;
      noiseBurst(dur, isOpen ? 8000 : 10000, isOpen ? 3 : 8, 0.04, t, musicGain);
    }

    // War tom fills
    if (bar % 4 === 3 && beat >= 12) {
      const tomFreq = 120 - (beat - 12) * 15;
      const tom = c2.createOscillator();
      const tG = c2.createGain();
      tom.type = 'sine';
      tom.frequency.setValueAtTime(tomFreq, t);
      tom.frequency.exponentialRampToValueAtTime(tomFreq * 0.4, t + 0.2);
      tG.gain.setValueAtTime(0.18, t);
      tG.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      tom.connect(tG); tG.connect(musicGain);
      tom.start(t); tom.stop(t + 0.25);
    }

    // ── Tense string stabs ──
    if (beat === 0 || beat === 6 || beat === 10) {
      const root = [73.42, 82.41, 65.41][bar % 3];
      const chordNotes = [root, root * 6/5, root * 3/2];
      for (const note of chordNotes) {
        const strOsc = c2.createOscillator();
        const strG = c2.createGain();
        strOsc.type = 'sawtooth';
        strOsc.frequency.setValueAtTime(note * 2, t);
        strG.gain.setValueAtTime(0.035, t);
        strG.gain.setValueAtTime(0.035, t + 0.03);
        strG.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        const strF = c2.createBiquadFilter();
        strF.type = 'lowpass';
        strF.frequency.setValueAtTime(800, t);
        strOsc.connect(strF); strF.connect(strG); strG.connect(musicGain);
        strOsc.start(t); strOsc.stop(t + 0.25);
      }
    }

    // ── Battle melody (sparse, dramatic) ──
    if (beat === 0 || beat === 3 || beat === 7 || beat === 11) {
      currentPhrase = Math.floor(bar / 4) % 4;
      const phrases = [
        [7, 10, 12, 14],
        [14, 12, 10, 7],
        [10, 14, 7, 12],
        [12, 7, 14, 10],
      ];
      const noteIdx = phrases[currentPhrase][[0,3,7,11].indexOf(beat)] ?? Math.floor(Math.random() * scale.length);
      const freq = scale[Math.min(noteIdx, scale.length - 1)];

      // Lead voice (filtered saw)
      const melOsc = c2.createOscillator();
      const melG = c2.createGain();
      melOsc.type = 'sawtooth';
      melOsc.frequency.setValueAtTime(freq * 2, t);
      melG.gain.setValueAtTime(0.04, t);
      melG.gain.setValueAtTime(0.04, t + 0.05);
      melG.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      const melF = c2.createBiquadFilter();
      melF.type = 'lowpass';
      melF.frequency.setValueAtTime(1200, t);
      melOsc.connect(melF); melF.connect(melG); melG.connect(musicGain);
      melOsc.start(t); melOsc.stop(t + 0.4);

      // Parallel octave below
      const melOsc2 = c2.createOscillator();
      const melG2 = c2.createGain();
      melOsc2.type = 'triangle';
      melOsc2.frequency.setValueAtTime(freq, t);
      melG2.gain.setValueAtTime(0.03, t);
      melG2.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      melOsc2.connect(melG2); melG2.connect(musicGain);
      melOsc2.start(t); melOsc2.stop(t + 0.35);
    }

    // ── Bass root shift every 8 bars ──
    if (beatCount % (16 * 2) === 0) {
      const roots = [55, 65.41, 73.42, 55];
      const root = roots[Math.floor(bar / 2) % roots.length];
      bassOsc1.frequency.exponentialRampToValueAtTime(root, c2.currentTime + 0.5);
      bassOsc2.frequency.exponentialRampToValueAtTime(root, c2.currentTime + 0.5);
      subOsc.frequency.exponentialRampToValueAtTime(root * 0.5, c2.currentTime + 0.5);
    }

    beatCount++;
  }, beatMs);
}

export function stopMusic() {
  musicPlaying = false;
  if (musicInterval) { clearInterval(musicInterval); musicInterval = null; }
  for (const node of musicNodes) {
    try { node.stop(); } catch {}
  }
  musicNodes = [];
}

export function setMusicVolume(v) {
  ensureCtx();
  musicGain.gain.setValueAtTime(Math.max(0, Math.min(1, v)), ctx.currentTime);
}

export function setSfxVolume(v) {
  ensureCtx();
  sfxGain.gain.setValueAtTime(Math.max(0, Math.min(1, v)), ctx.currentTime);
}

/** Call once on first user interaction to unlock audio */
export function unlock() { ensureCtx(); }
